import React from 'react';
import { ChatHelper, ReadReceiptHelper } from '../helpers';
import {
  ChatMessage,
  ChatProcess,
  LiveChatData,
  ScreenType,
  SocketChatSession
} from '../types';
import ChatWaitingMiddle from './ChatWaitingMiddle';
import MessageContent from './MessageContent';
import MessageLiveChat from './MessageLiveChat';
import MessageLiveEnd from './MessageLiveEnd';
import MessageUnreadDivider from './MessageUnreadDivider';
import MessageUser from './MessageUser';
import TimeText from './TimeText';
import TypingIndicator from './TypingIndicator';

interface ChatMessageTimelineProps {
  messages: ChatMessage[];
  screenType: ScreenType;
  chatHelper: ChatHelper | null;
  chatData?: LiveChatData | null;
  isPendingSession: boolean;
  isLoadingSession: boolean;
  assignProcess: ChatProcess;
  messagesListRef?: React.Ref<HTMLDivElement>;
  messagesContentRef?: React.Ref<HTMLDivElement>;
  showTypingIndicator?: boolean;
  typingIndicatorCurrentRef?: React.MutableRefObject<SocketChatSession | null>;
  hasSocketError?: boolean;
  clientTypingSessions?: Record<string, boolean>;
  emptyMessage?: React.ReactNode;
  firstUnreadMessageId?: string | null;
  registerUnreadMessageRef?: (messageId: string, isUnread: boolean) => (element: HTMLDivElement | null) => void;
  /** Optional extras under each message bubble (e.g. report prompts / choice answers). */
  renderMessageExtras?: (message: ChatMessage) => React.ReactNode;
  /** Optional suffix after the message user name (e.g. report bot model name). */
  renderMessageUserSuffix?: (message: ChatMessage) => React.ReactNode;
  /** When true, skip the text/file bubble for that message (extras can replace it). */
  skipMessageContent?: (message: ChatMessage) => boolean;
}

const ChatMessageTimeline: React.FC<ChatMessageTimelineProps> = ({
  messages,
  screenType,
  chatHelper,
  chatData = null,
  isPendingSession,
  isLoadingSession,
  assignProcess,
  messagesListRef,
  messagesContentRef,
  showTypingIndicator = false,
  typingIndicatorCurrentRef,
  hasSocketError = false,
  clientTypingSessions = {},
  emptyMessage = null,
  firstUnreadMessageId = null,
  registerUnreadMessageRef,
  renderMessageExtras,
  renderMessageUserSuffix,
  skipMessageContent
}) => {
  // Keep cached messages visible during session reload (e.g. tab visibility resync).
  // Only show the skeleton placeholder when we have nothing to display yet.
  const showLoadingSkeleton = isLoadingSession && messages.length === 0;
  const shouldRenderMessages = !isPendingSession && !showLoadingSkeleton;

  return (
    <div className="chatyme-messages-list" ref={messagesListRef}>
      <div className="chatyme-messages-list-content" ref={messagesContentRef}>
      <ChatWaitingMiddle
        isPendingSession={isPendingSession}
        assignProcess={assignProcess}
        isLoadingSession={showLoadingSkeleton}
      />
      {shouldRenderMessages && messages.length === 0 && emptyMessage}
      {shouldRenderMessages && messages.map((msg) => {
        const isUnread = ReadReceiptHelper.isUnreadFromCounterparty(msg, screenType);

        return (
          <React.Fragment key={msg.id}>
            {firstUnreadMessageId === msg.id && (
              <MessageUnreadDivider chatData={chatData} screenType={screenType} />
            )}
            <div
              className="chatyme-message-group"
              data-message-id={msg.id}
              ref={registerUnreadMessageRef?.(msg.id, isUnread)}
            >
              <TimeText
                screenType={screenType}
                chatHelper={chatHelper}
                message={msg}
                chatData={chatData}
              />
              <MessageUser
                screenType={screenType}
                chatHelper={chatHelper}
                message={msg}
                chatData={chatData}
                renderUserNameSuffix={renderMessageUserSuffix}
              />
              {!skipMessageContent?.(msg) && (
                <MessageContent screenType={screenType} chatHelper={chatHelper} message={msg} />
              )}
              {renderMessageExtras?.(msg)}
              <MessageLiveChat
                chatHelper={chatHelper}
                chatData={chatData}
                screenType={screenType}
                message={msg}
              />
              <MessageLiveEnd chatHelper={chatHelper} message={msg} chatData={chatData} screenType={screenType} />
            </div>
          </React.Fragment>
        );
      })}

      {showTypingIndicator && typingIndicatorCurrentRef && (
        <TypingIndicator
          screenType={screenType}
          currentCRef={typingIndicatorCurrentRef}
          hasSocketError={hasSocketError}
          clientTypingSessions={clientTypingSessions}
          chatData={chatData}
        />
      )}
      </div>
    </div>
  );
};

export default ChatMessageTimeline;
